import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Header, Icon, Segment, Divider, Loader, Message, Button } from "semantic-ui-react";
import { format } from "date-fns";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { functionGetStrategyGenerationResult } from "../../../../app/firestore/functionsService";
import StrategyFeedback from "./StrategyFeedback";
import { buildPricingFeedbackConfig } from "./pricingFeedbackConfig";
import PricingStrategyResultBody from "./PricingStrategyResultBody";
import { buildDynamicDisclaimer } from "./pricingStrategyFormatting";

// A Firestore Timestamp returned through a Cloud Functions onCall response
// serializes as a plain {_seconds, _nanoseconds}-shaped object with no
// .toDate() method (not the client SDK's Timestamp class) - new Date() on
// that produces an Invalid Date, and date-fns' format() throws a hard
// RangeError on it rather than failing quietly. Guard before formatting.
function toDisplayDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy");
}

// Read-only viewer for a PREVIOUSLY generated Strategy on a property that is
// no longer the transaction's current address (spec sections 29-30: a
// Buyer's old properties remain viewable at no additional cost). Regenerating
// an old property isn't supported here - making it the transaction's current
// address again routes back through the full StrategyPricingModal flow.
export default function StrategyResultViewerModal({ transaction, transactionId, propertyKey, propertyAddress, generationId, agentRepresents }) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [generation, setGeneration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    functionGetStrategyGenerationResult({ transactionId, generationId })
      .then((data) => {
        if (!cancelled) setGeneration(data);
      })
      .catch(() => {
        if (!cancelled) setGeneration(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transactionId, generationId]);

  function handleClose() {
    dispatch(closeModal({ modalType: "StrategyResultViewerModal" }));
  }

  const result = generation?.result;
  // Prefer the representation this Strategy was actually generated under
  // (generation.agentRepresents, persisted since functions/strategyCredits/
  // representation.js) over the transaction's current value - they can
  // differ (e.g. agentRepresents corrected after generation), and per spec
  // section 28 a saved Strategy's feedback context should reflect how it was
  // actually produced, not be reinterpreted against a since-changed field.
  // Falls back to the caller-supplied current value only for generations
  // saved before this field existed.
  const isBuyer = (generation?.agentRepresents || agentRepresents) === "Buyer";

  // Print letterhead - identical to StrategyPricingModal's (agent logo, full
  // name, brokerage, phone, email), so a previous property's Strategy prints
  // the same way the current one does rather than looking like a different
  // document.
  const letterheadAgentName = `${
    transaction?.agentProfile?.firstName || currentUserProfile?.firstName || ""
  } ${transaction?.agentProfile?.lastName || currentUserProfile?.lastName || ""}`.trim();
  const letterheadBrokerageName = transaction?.agentProfile?.brokerageName || currentUserProfile?.brokerageName || "";
  const letterheadPhone = transaction?.agentProfile?.phone || currentUserProfile?.phone || "";
  const letterheadEmail = transaction?.agentProfile?.email || currentUserProfile?.email || "";
  const letterheadLogoUrl = transaction?.agentProfile?.brokerageLogoRef || currentUserProfile?.brokerageLogoRef || null;

  return (
    <ModalWrapper size="large" header="Previous Property Strategy">
      <Segment clearing id="strategy-result-viewer-print-area">
        <div
          className="print-only"
          style={{ marginBottom: "16px", paddingBottom: "12px", borderBottom: "2px solid #2185d0" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {letterheadLogoUrl && (
              <img src={letterheadLogoUrl} alt="Brokerage logo" style={{ maxHeight: "60px", maxWidth: "160px" }} />
            )}
            <div>
              {letterheadAgentName && <div style={{ fontSize: "1.1em", fontWeight: 700 }}>{letterheadAgentName}</div>}
              {letterheadBrokerageName && <div style={{ fontSize: "0.95em" }}>{letterheadBrokerageName}</div>}
              {(letterheadPhone || letterheadEmail) && (
                <div style={{ fontSize: "0.85em", color: "#555" }}>
                  {letterheadPhone}
                  {letterheadPhone && letterheadEmail ? " · " : ""}
                  {letterheadEmail}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Header color="blue" size="large" style={{ marginBottom: "4px" }}>
              <Icon name="dollar" />
              {isBuyer ? "Offer Pricing Strategy" : "List Pricing Strategy"}
            </Header>
            <p className="text-medium" style={{ margin: 0 }}>
              <b>{propertyAddress?.street}</b>
              {propertyAddress?.unit ? ` ${propertyAddress.unit}` : ""}
              <br />
              {propertyAddress?.city}, {propertyAddress?.state}
            </p>
            {toDisplayDate(generation?.createdAt) && (
              <div className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                Generated {toDisplayDate(generation.createdAt)}
              </div>
            )}
            {(result?.inputs?.currentPrice || result?.inputs?.timeOnMarket) && (
              <div style={{ fontSize: "0.92em", marginTop: "4px" }}>
                {result.inputs?.currentPrice && (
                  <span>
                    <span className="text-grey">Current List Price:</span> <strong>{result.inputs.currentPrice}</strong>
                  </span>
                )}
                {result.inputs?.currentPrice && result.inputs?.timeOnMarket && <span className="text-grey"> &nbsp;&middot;&nbsp; </span>}
                {result.inputs?.timeOnMarket && (
                  <span>
                    <span className="text-grey">Approx. Days on Market:</span> <strong>{result.inputs.timeOnMarket}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
          <Button basic icon labelPosition="left" size="small" className="no-print" onClick={() => window.print()}>
            <Icon name="print" />
            Print
          </Button>
        </div>
        <Divider />
        {loading && <Loader active inline="centered" content="Loading..." />}
        {!loading && !result && <p className="text-grey">No saved result found for this property.</p>}
        {!loading && result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <PricingStrategyResultBody result={result} isBuyer={isBuyer} />

            {result.clientExplanation && (
              <div className="no-print">
                <Header as="h4">Explanation</Header>
                <p style={{ whiteSpace: "pre-wrap" }}>{result.clientExplanation}</p>
              </div>
            )}

            <Message info size="small">
              <Icon name="info circle" />
              {buildDynamicDisclaimer(result?.sourcesUsed)}
            </Message>

            <Divider className="no-print" />
            <div className="no-print">
              <StrategyFeedback
                transactionId={transactionId}
                propertyKey={propertyKey}
                propertyAddress={propertyAddress}
                strategyType="pricing"
                generationId={generationId}
                generatedRecommendationSnapshot={{
                  recommendedListPrice: result.recommendedListPrice,
                  estimatedValueLow: result.estimatedValueLow,
                  estimatedValueHigh: result.estimatedValueHigh,
                  confidence: result.confidence,
                }}
                engineMetadata={{ modelName: generation.modelName || null }}
                config={buildPricingFeedbackConfig(isBuyer)}
              />
            </div>
          </div>
        )}
        <Divider className="medium top margin no-print" />
        <Button className="no-print" onClick={handleClose}>Close</Button>
      </Segment>
    </ModalWrapper>
  );
}
